import React from "react";
import { Link } from "react-router-dom";
import { FaInstagram, FaYoutube, FaEnvelope, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { LOGO_URL, SOCIAL, EMAILS } from "@/lib/brand";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import FooterLogoRail from "@/components/LegalMarks";

// Arabic Footer — RTL localization of the approved English Footer.
// Protected institutional assets (CrewZNatioN, The Gathering, Smiles
// Per Liter, Eat · Sleep · CrewZ · Repeat, apply.crewznation.com,
// email addresses) remain in English per PRD §34.2 and are
// LTR-isolated inside the RTL flow.

export default function FooterAr() {
    return (
        <footer
            className="border-t border-white/5 bg-[var(--crewz-bg)] mt-32"
            data-testid="site-footer-ar"
            dir="rtl"
        >
            <div className="px-6 md:px-10 lg:px-16 py-20">
                <FooterLogoRail locale="ar" />

                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-6">
                        <p className="font-arabic text-base text-zinc-400 max-w-md leading-[1.9]">
                            مؤسسة متجذّرة في الرياض، تُكرّس نفسها لثقافة المركبات الاستثنائية والأشخاص الذين يعتنون بها.
                        </p>
                    </div>

                <div className="md:col-span-3">
                    <p className="overline mb-5 text-zinc-500 font-arabic !text-[20.04px] md:!text-[21.8px]">استكشف</p>
                    <ul className="font-arabic text-sm space-y-3 text-zinc-300">
                        <li>
                            <Link to="/ar/about" className="hover:text-[var(--crewz-gold)] transition-colors">
                                عن المؤسسة
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/gathering" className="hover:text-[var(--crewz-gold)] transition-colors" dir="ltr">
                                <EventName />
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/membership" className="hover:text-[var(--crewz-gold)] transition-colors">
                                العضوية
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/sponsors" className="hover:text-[var(--crewz-gold)] transition-colors">
                                الرعاة
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/rules" className="hover:text-[var(--crewz-gold)] transition-colors">
                                الأنظمة واللوائح
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/privacy" className="hover:text-[var(--crewz-gold)] transition-colors" data-testid="footer-privacy-ar">
                                سياسة الخصوصية
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/account-deletion" className="hover:text-[var(--crewz-gold)] transition-colors" data-testid="footer-account-deletion-ar">
                                حذف الحساب
                            </Link>
                        </li>
                        <li>
                            <Link to="/ar/terms" className="hover:text-[var(--crewz-gold)] transition-colors" data-testid="footer-terms-ar">
                                شروط الخدمة
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <p className="overline mb-5 text-zinc-500 font-arabic !text-[20.04px] md:!text-[21.8px]">تواصل</p>
                    <ul className="font-arabic text-sm space-y-3 text-zinc-300 text-left">
                        <li>
                            <a
                                href={`mailto:${EMAILS.primary}`}
                                dir="ltr"
                                className="inline-flex items-center gap-2.5 min-w-0 max-w-full hover:text-[var(--crewz-gold)] transition-colors"
                                data-testid="footer-email-primary-ar"
                            >
                                <FaEnvelope size={17} className="shrink-0 text-zinc-400" /> <span className="break-all leading-tight" dir="ltr">{EMAILS.primary}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${EMAILS.admin}`}
                                dir="ltr"
                                className="inline-flex items-center gap-2.5 min-w-0 max-w-full hover:text-[var(--crewz-gold)] transition-colors"
                            >
                                <FaEnvelope size={17} className="shrink-0 text-zinc-400" /> <span className="break-all leading-tight" dir="ltr">{EMAILS.admin}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={SOCIAL.registration}
                                target="_blank"
                                rel="noreferrer"
                                dir="ltr"
                                className="inline-flex items-center gap-2.5 min-w-0 max-w-full hover:text-[var(--crewz-gold)] transition-colors"
                                data-testid="footer-registration-ar"
                            >
                                <FaArrowUpRightFromSquare size={16} className="shrink-0 text-zinc-400" /> <span className="break-all leading-tight" dir="ltr">apply.crewznation.com</span>
                            </a>
                        </li>
                    </ul>

                    <div className="mt-6 text-zinc-300 space-y-3 text-left">
                        <div>
                            <a
                                href={SOCIAL.instagram}
                                target="_blank"
                                rel="noreferrer"
                                dir="ltr"
                                aria-label="Instagram — @CrewZNatioN"
                                className="inline-flex items-center gap-2.5 hover:text-[var(--crewz-gold)] transition-colors"
                                data-testid="footer-instagram-ar"
                            >
                                <FaInstagram size={26} className="shrink-0" style={{ color: "#E1306C" }} />
                                <span className="font-body text-sm leading-tight" dir="ltr">@CrewZNatioN</span>
                            </a>
                        </div>
                        <div>
                            <a
                                href={SOCIAL.youtube}
                                target="_blank"
                                rel="noreferrer"
                                dir="ltr"
                                aria-label="YouTube — @CrewZNatioN"
                                className="inline-flex items-center gap-2.5 hover:text-[var(--crewz-gold)] transition-colors"
                                data-testid="footer-youtube-ar"
                            >
                                <FaYoutube size={26} className="shrink-0" style={{ color: "#FF0000" }} />
                                <span className="font-body text-sm leading-tight" dir="ltr">@CrewZNatioN</span>
                            </a>
                        </div>
                    </div>
                </div>
                </div>
            </div>

            <div className="border-t border-white/5 px-6 md:px-10 lg:px-16 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <p className="font-arabic text-xs text-zinc-600">
                    © {new Date().getFullYear()} <Brand /> جميع الحقوق محفوظة.
                </p>
                <p
                    dir="ltr"
                    className="font-body text-xs text-zinc-500 tracking-[0.18em] uppercase"
                    data-testid="footer-signature-expressions-ar"
                >
                    Smiles Per Liter <span className="text-[var(--crewz-gold)] mx-2">·</span> Eat <span className="mx-1">·</span> Sleep <span className="mx-1">·</span> CrewZ <span className="mx-1">·</span> Repeat
                </p>
                <p className="font-arabic text-xs text-zinc-600">
                    الرياض · المملكة العربية السعودية
                </p>
            </div>
        </footer>
    );
}
